// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { 
  getFeeSchedules, 
  createFeeSchedule, 
  updateFeeSchedule, 
  deleteFeeSchedule, 
  approveFeeSchedule, 
  getBanks, 
  getRetailStores,
  getTier3Operators,
  calculateFee
} from "../lib/api";
import { toast } from "sonner";
import { 
  Plus, 
  DollarSign, 
  Pencil, 
  Trash2, 
  CheckCircle2, 
  Clock,
  XCircle,
  RefreshCw,
  Building2,
  Store,
  Network,
  Shield,
  Copy,
  Calculator,
  History,
  ChevronRight,
  AlertCircle,
  FileCheck,
  Send,
  Eye,
  MoreHorizontal
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const EVENT_TYPES = [
  { value: "authorization_created", label: "Authorization Created" },
  { value: "authorization_cancelled", label: "Authorization Cancelled" },
  { value: "cash_withdrawal_success", label: "Cash Withdrawal Success" },
  { value: "cash_withdrawal_reversal", label: "Cash Withdrawal Reversal" },
  { value: "voucher_issued", label: "Voucher Issued" },
  { value: "voucher_redeemed", label: "Voucher Redeemed" },
  { value: "settlement_run", label: "Settlement Run" },
  { value: "monthly_platform_fee", label: "Monthly Platform Fee" }
];

const COUNTERPARTY_TYPES = [
  { value: "bank", label: "Bank (Issuer)", icon: Building2 },
  { value: "atm_operator", label: "ATM Operator", icon: Network },
  { value: "retailer", label: "Retailer", icon: Store }
];

const CHARGE_MODELS = [
  { value: "fixed", label: "Fixed Amount" },
  { value: "percentage", label: "Percentage" },
  { value: "tiered", label: "Tiered" }
];

const WHO_PAYS_OPTIONS = [
  { value: "issuer", label: "Issuer" },
  { value: "acquirer", label: "Acquirer" },
  { value: "both", label: "Split" }
];

const VAT_OPTIONS = [
  { value: "exclusive", label: "VAT Exclusive" },
  { value: "inclusive", label: "VAT Inclusive" },
  { value: "exempt", label: "VAT Exempt" }
];

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "—";
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(amount);
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    pending_approval: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    active: "bg-green-500/20 text-green-400 border-green-500/30",
    retired: "bg-gray-500/20 text-gray-400 border-gray-500/30"
  };
  const icons = {
    draft: Clock,
    pending_approval: Send,
    active: CheckCircle2,
    retired: XCircle
  };
  const labels = {
    draft: "Draft",
    pending_approval: "Pending Approval",
    active: "Active",
    retired: "Retired"
  };
  const Icon = icons[status] || Clock;
  
  return (
    <Badge variant="outline" className={styles[status] || styles.draft}>
      <Icon className="h-3 w-3 mr-1" />
      {labels[status] || status?.charAt(0).toUpperCase() + status?.slice(1)}
    </Badge>
  );
};

const defaultFeeLine = () => ({
  id: crypto.randomUUID(),
  event_type: "cash_withdrawal_success",
  charge_model: "fixed",
  fixed_amount: 0,
  percentage: null,
  tiered_rates: null,
  min_cap: null,
  max_cap: null,
  who_pays: "issuer",
  split_issuer_percent: 50,
  split_acquirer_percent: 50,
  vat_treatment: "exclusive",
  vat_rate: 15
});

const FeeSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [banks, setBanks] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [tier3Operators, setTier3Operators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isAuditLogOpen, setIsAuditLogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  // 2-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Header Form State
  const [headerForm, setHeaderForm] = useState({
    name: "",
    counterparty_type: "bank",
    counterparty_id: "",
    currency: "ZAR",
    status: "draft",
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: "",
    minimum_monthly: "",
    notes: ""
  });

  // Fee Lines State
  const [feeLines, setFeeLines] = useState([defaultFeeLine()]);

  // Preview Fee State
  const [previewAmount, setPreviewAmount] = useState("500");
  const [previewEventType, setPreviewEventType] = useState("cash_withdrawal_success");
  const [previewResult, setPreviewResult] = useState(null);

  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    fetchData();
  }, [filterType, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.counterparty_type = filterType;
      if (filterStatus !== "all") params.status = filterStatus;
      
      const [schedulesRes, banksRes, retailersRes, tier3Res] = await Promise.all([
        getFeeSchedules(params),
        getBanks(),
        getRetailStores(),
        getTier3Operators()
      ]);
      setSchedules(schedulesRes.data || []);
      setBanks(banksRes.data || []);
      setRetailers(retailersRes.data || []);
      setTier3Operators(tier3Res.data || []);
    } catch (error) {
      toast.error("Failed to load fee schedules");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setHeaderForm({
      name: "",
      counterparty_type: "bank",
      counterparty_id: "",
      currency: "ZAR",
      status: "draft",
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: "",
      minimum_monthly: "",
      notes: ""
    });
    setFeeLines([defaultFeeLine()]);
    setCurrentStep(1);
    setEditingSchedule(null);
    setPreviewResult(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const openEditModal = (schedule) => {
    setEditingSchedule(schedule);
    setHeaderForm({
      name: schedule.name,
      counterparty_type: schedule.counterparty_type,
      counterparty_id: schedule.counterparty_id || "",
      currency: schedule.currency || "ZAR",
      status: schedule.status,
      effective_from: schedule.effective_from?.split('T')[0] || "",
      effective_to: schedule.effective_to?.split('T')[0] || "",
      minimum_monthly: schedule.minimum_monthly || "",
      notes: schedule.notes || ""
    });
    setFeeLines(schedule.fee_lines?.length > 0 
      ? schedule.fee_lines.map(fl => ({ ...fl, id: fl.id || crypto.randomUUID() }))
      : [defaultFeeLine()]
    );
    setCurrentStep(1);
    setIsCreateModalOpen(true);
  };

  const openCloneModal = (schedule) => {
    setSelectedSchedule(schedule);
    setHeaderForm({
      name: `${schedule.name} (Copy)`,
      counterparty_type: schedule.counterparty_type,
      counterparty_id: schedule.counterparty_id || "",
      currency: schedule.currency || "ZAR",
      status: "draft",
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: "",
      minimum_monthly: schedule.minimum_monthly || "",
      notes: schedule.notes || ""
    });
    setFeeLines(schedule.fee_lines?.length > 0 
      ? schedule.fee_lines.map(fl => ({ ...fl, id: crypto.randomUUID() }))
      : [defaultFeeLine()]
    );
    setIsCloneModalOpen(true);
  };

  const openActivateModal = (schedule) => {
    setSelectedSchedule(schedule);
    setHeaderForm(prev => ({
      ...prev,
      effective_from: schedule.effective_from?.split('T')[0] || new Date().toISOString().split('T')[0],
      effective_to: schedule.effective_to?.split('T')[0] || ""
    }));
    setIsActivateModalOpen(true);
  };

  const openApproveModal = (schedule) => {
    setSelectedSchedule(schedule);
    setIsApproveModalOpen(true);
  };

  const openAuditLog = (schedule) => {
    setSelectedSchedule(schedule);
    // Mock audit logs for now - would come from API
    setAuditLogs([
      { action: "Created", user: schedule.created_by || "admin", timestamp: schedule.created_at, details: "Initial creation" },
      ...(schedule.approved_at ? [{ action: "Approved", user: schedule.approved_by || "admin", timestamp: schedule.approved_at, details: "Approved and activated" }] : [])
    ]);
    setIsAuditLogOpen(true);
  };

  const handleSubmit = async (asDraft = true) => {
    if (!headerForm.name) {
      toast.error("Please enter a schedule name");
      return;
    }

    const payload = {
      ...headerForm,
      status: asDraft ? "draft" : "pending_approval",
      effective_from: new Date(headerForm.effective_from).toISOString(),
      effective_to: headerForm.effective_to ? new Date(headerForm.effective_to).toISOString() : null,
      minimum_monthly: headerForm.minimum_monthly ? parseFloat(headerForm.minimum_monthly) : null,
      counterparty_id: headerForm.counterparty_id || null,
      fee_lines: feeLines.map(line => ({
        event_type: line.event_type,
        charge_model: line.charge_model,
        fixed_amount: line.charge_model === "fixed" ? parseFloat(line.fixed_amount) || null : null,
        percentage: line.charge_model === "percentage" ? parseFloat(line.percentage) || null : null,
        tiered_rates: line.charge_model === "tiered" ? line.tiered_rates : null,
        min_cap: line.min_cap ? parseFloat(line.min_cap) : null,
        max_cap: line.max_cap ? parseFloat(line.max_cap) : null,
        who_pays: line.who_pays,
        split_issuer_percent: line.who_pays === "both" ? parseFloat(line.split_issuer_percent) || 50 : null,
        split_acquirer_percent: line.who_pays === "both" ? parseFloat(line.split_acquirer_percent) || 50 : null,
        vat_treatment: line.vat_treatment,
        vat_rate: parseFloat(line.vat_rate) || 15
      }))
    };

    try {
      if (editingSchedule) {
        await updateFeeSchedule(editingSchedule.id, payload);
        toast.success(asDraft ? "Fee schedule saved as draft" : "Fee schedule submitted for approval");
      } else {
        await createFeeSchedule(payload);
        toast.success(asDraft ? "Fee schedule created as draft" : "Fee schedule submitted for approval");
      }
      setIsCreateModalOpen(false);
      setIsCloneModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save fee schedule");
    }
  };

  const handleSubmitForApproval = async (schedule) => {
    try {
      await updateFeeSchedule(schedule.id, { status: "pending_approval" });
      toast.success("Fee schedule submitted for approval");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit for approval");
    }
  };

  const handleClone = async () => {
    const payload = {
      ...headerForm,
      status: "draft",
      effective_from: new Date(headerForm.effective_from).toISOString(),
      effective_to: headerForm.effective_to ? new Date(headerForm.effective_to).toISOString() : null,
      minimum_monthly: headerForm.minimum_monthly ? parseFloat(headerForm.minimum_monthly) : null,
      counterparty_id: headerForm.counterparty_id || null,
      fee_lines: feeLines.map(line => ({
        event_type: line.event_type,
        charge_model: line.charge_model,
        fixed_amount: line.charge_model === "fixed" ? parseFloat(line.fixed_amount) || null : null,
        percentage: line.charge_model === "percentage" ? parseFloat(line.percentage) || null : null,
        tiered_rates: line.charge_model === "tiered" ? line.tiered_rates : null,
        min_cap: line.min_cap ? parseFloat(line.min_cap) : null,
        max_cap: line.max_cap ? parseFloat(line.max_cap) : null,
        who_pays: line.who_pays,
        split_issuer_percent: line.who_pays === "both" ? parseFloat(line.split_issuer_percent) || 50 : null,
        split_acquirer_percent: line.who_pays === "both" ? parseFloat(line.split_acquirer_percent) || 50 : null,
        vat_treatment: line.vat_treatment,
        vat_rate: parseFloat(line.vat_rate) || 15
      }))
    };

    try {
      await createFeeSchedule(payload);
      toast.success("Fee schedule cloned successfully");
      setIsCloneModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clone fee schedule");
    }
  };

  const handleActivate = async () => {
    try {
      await updateFeeSchedule(selectedSchedule.id, {
        status: "active",
        effective_from: new Date(headerForm.effective_from).toISOString(),
        effective_to: headerForm.effective_to ? new Date(headerForm.effective_to).toISOString() : null
      });
      toast.success("Fee schedule activated");
      setIsActivateModalOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate fee schedule");
    }
  };

  const handleApprove = async () => {
    try {
      await approveFeeSchedule(selectedSchedule.id);
      toast.success("Fee schedule approved and activated");
      setIsApproveModalOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve fee schedule");
    }
  };

  const handleDelete = async (schedule) => {
    if (!confirm(`Delete fee schedule "${schedule.name}"?`)) return;
    
    try {
      await deleteFeeSchedule(schedule.id);
      toast.success("Fee schedule deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handleRetire = async (schedule) => {
    if (!confirm(`Retire fee schedule "${schedule.name}"? It will no longer be used for new transactions.`)) return;
    
    try {
      await updateFeeSchedule(schedule.id, { status: "retired" });
      toast.success("Fee schedule retired");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retire fee schedule");
    }
  };

  // Fee Line Management
  const addFeeLine = () => {
    setFeeLines([...feeLines, defaultFeeLine()]);
  };

  const duplicateFeeLine = (index) => {
    const lineToCopy = { ...feeLines[index], id: crypto.randomUUID() };
    const newLines = [...feeLines];
    newLines.splice(index + 1, 0, lineToCopy);
    setFeeLines(newLines);
  };

  const removeFeeLine = (index) => {
    if (feeLines.length <= 1) {
      toast.error("At least one fee line is required");
      return;
    }
    setFeeLines(feeLines.filter((_, i) => i !== index));
  };

  const updateFeeLine = (index, field, value) => {
    const updated = [...feeLines];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-adjust split percentages
    if (field === "split_issuer_percent") {
      updated[index].split_acquirer_percent = 100 - (parseFloat(value) || 0);
    } else if (field === "split_acquirer_percent") {
      updated[index].split_issuer_percent = 100 - (parseFloat(value) || 0);
    }
    
    setFeeLines(updated);
  };

  const getCounterpartyOptions = () => {
    if (headerForm.counterparty_type === "bank") {
      return banks.map(b => ({ value: b.bank_code || b.id, label: `${b.bank_code} - ${b.bank_name}` }));
    } else if (headerForm.counterparty_type === "retailer") {
      return retailers.map(r => ({ value: r.issuer_code || r.id, label: `${r.issuer_code} - ${r.display_name || r.name}` }));
    } else if (headerForm.counterparty_type === "atm_acquirer") {
      return tier3Operators.map(o => ({ value: o.operator_id || o.id, label: `${o.operator_id} - ${o.operator_name}` }));
    }
    return [];
  };

  // Calculate preview fee
  const calculatePreviewFee = useCallback(() => {
    const amount = parseFloat(previewAmount) || 0;
    const matchingLine = feeLines.find(fl => fl.event_type === previewEventType);
    
    if (!matchingLine) {
      setPreviewResult({ error: "No fee line for this event type" });
      return;
    }

    let baseFee = 0;
    if (matchingLine.charge_model === "fixed") {
      baseFee = parseFloat(matchingLine.fixed_amount) || 0;
    } else if (matchingLine.charge_model === "percentage") {
      baseFee = (amount * (parseFloat(matchingLine.percentage) || 0)) / 100;
    } else if (matchingLine.charge_model === "tiered" && matchingLine.tiered_rates) {
      // Find matching tier
      const tier = matchingLine.tiered_rates.find(t => amount >= t.min && amount <= t.max);
      baseFee = tier ? tier.amount : 0;
    }

    // Apply caps
    if (matchingLine.min_cap && baseFee < matchingLine.min_cap) {
      baseFee = parseFloat(matchingLine.min_cap);
    }
    if (matchingLine.max_cap && baseFee > matchingLine.max_cap) {
      baseFee = parseFloat(matchingLine.max_cap);
    }

    // Calculate VAT
    let vatAmount = 0;
    let totalFee = baseFee;
    if (matchingLine.vat_treatment === "exclusive") {
      vatAmount = baseFee * (parseFloat(matchingLine.vat_rate) || 15) / 100;
      totalFee = baseFee + vatAmount;
    } else if (matchingLine.vat_treatment === "inclusive") {
      vatAmount = baseFee - (baseFee / (1 + (parseFloat(matchingLine.vat_rate) || 15) / 100));
    }

    // Calculate split if applicable
    let issuerPays = totalFee;
    let acquirerPays = 0;
    if (matchingLine.who_pays === "acquirer") {
      issuerPays = 0;
      acquirerPays = totalFee;
    } else if (matchingLine.who_pays === "both") {
      const issuerPercent = parseFloat(matchingLine.split_issuer_percent) || 50;
      issuerPays = totalFee * issuerPercent / 100;
      acquirerPays = totalFee - issuerPays;
    }

    setPreviewResult({
      baseFee,
      vatAmount,
      totalFee,
      issuerPays,
      acquirerPays,
      whoPays: matchingLine.who_pays,
      chargeModel: matchingLine.charge_model
    });
  }, [previewAmount, previewEventType, feeLines]);

  useEffect(() => {
    if (currentStep === 2) {
      calculatePreviewFee();
    }
  }, [currentStep, previewAmount, previewEventType, feeLines, calculatePreviewFee]);

  // Render Fee Lines Table (Inline Editing)
  const renderFeeLineTable = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">Fee Lines</h3>
          <p className="text-xs text-muted-foreground">Configure fees for each event type</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addFeeLine} data-testid="add-fee-line-btn">
          <Plus className="h-4 w-4 mr-1" />
          Add Row
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="min-w-[160px]">Event Type</TableHead>
                <TableHead className="min-w-[120px]">Charge Model</TableHead>
                <TableHead className="min-w-[100px]">Amount/Rate</TableHead>
                <TableHead className="min-w-[80px]">Min Cap</TableHead>
                <TableHead className="min-w-[80px]">Max Cap</TableHead>
                <TableHead className="min-w-[100px]">Who Pays</TableHead>
                <TableHead className="min-w-[80px]">Split %</TableHead>
                <TableHead className="min-w-[100px]">VAT</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feeLines.map((line, index) => (
                <TableRow key={line.id} data-testid={`fee-line-row-${index}`}>
                  <TableCell className="p-1">
                    <Select value={line.event_type} onValueChange={(v) => updateFeeLine(index, "event_type", v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map(et => (
                          <SelectItem key={et.value} value={et.value} className="text-xs">{et.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="p-1">
                    <Select value={line.charge_model} onValueChange={(v) => updateFeeLine(index, "charge_model", v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHARGE_MODELS.map(cm => (
                          <SelectItem key={cm.value} value={cm.value} className="text-xs">{cm.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="p-1">
                    {line.charge_model === "fixed" && (
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 text-xs w-20"
                        value={line.fixed_amount || ""}
                        onChange={(e) => updateFeeLine(index, "fixed_amount", e.target.value)}
                        placeholder="R0.00"
                      />
                    )}
                    {line.charge_model === "percentage" && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-xs w-16"
                          value={line.percentage || ""}
                          onChange={(e) => updateFeeLine(index, "percentage", e.target.value)}
                          placeholder="0.00"
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                    {line.charge_model === "tiered" && (
                      <span className="text-xs text-muted-foreground">Configure tiers</span>
                    )}
                  </TableCell>
                  <TableCell className="p-1">
                    <Input
                      type="number"
                      step="0.01"
                      className="h-8 text-xs w-16"
                      value={line.min_cap || ""}
                      onChange={(e) => updateFeeLine(index, "min_cap", e.target.value)}
                      placeholder="—"
                    />
                  </TableCell>
                  <TableCell className="p-1">
                    <Input
                      type="number"
                      step="0.01"
                      className="h-8 text-xs w-16"
                      value={line.max_cap || ""}
                      onChange={(e) => updateFeeLine(index, "max_cap", e.target.value)}
                      placeholder="—"
                    />
                  </TableCell>
                  <TableCell className="p-1">
                    <Select value={line.who_pays} onValueChange={(v) => updateFeeLine(index, "who_pays", v)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WHO_PAYS_OPTIONS.map(wp => (
                          <SelectItem key={wp.value} value={wp.value} className="text-xs">{wp.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="p-1">
                    {line.who_pays === "both" ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          className="h-8 text-xs w-12"
                          value={line.split_issuer_percent || 50}
                          onChange={(e) => updateFeeLine(index, "split_issuer_percent", e.target.value)}
                        />
                        <span className="text-xs text-muted-foreground">/</span>
                        <span className="text-xs">{line.split_acquirer_percent || 50}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="p-1">
                    <div className="flex items-center gap-1">
                      <Select value={line.vat_treatment} onValueChange={(v) => updateFeeLine(index, "vat_treatment", v)}>
                        <SelectTrigger className="h-8 text-xs w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VAT_OPTIONS.map(v => (
                            <SelectItem key={v.value} value={v.value} className="text-xs">{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="p-1">
                    <div className="flex items-center gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateFeeLine(index)}>
                              <Copy className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Duplicate Row</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeFeeLine(index)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete Row</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );

  // Render Preview Panel
  const renderPreviewPanel = () => (
    <Card className="bg-secondary/30 border-border h-fit sticky top-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calculator className="h-4 w-4 text-primary" />
          Fee Preview
        </CardTitle>
        <CardDescription className="text-xs">Test fee calculation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs">Transaction Amount (ZAR)</Label>
          <Input
            type="number"
            step="0.01"
            className="h-8"
            value={previewAmount}
            onChange={(e) => setPreviewAmount(e.target.value)}
            placeholder="500.00"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Event Type</Label>
          <Select value={previewEventType} onValueChange={setPreviewEventType}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map(et => (
                <SelectItem key={et.value} value={et.value} className="text-xs">{et.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {previewResult?.error ? (
          <div className="flex items-center gap-2 text-yellow-400 text-xs">
            <AlertCircle className="h-4 w-4" />
            {previewResult.error}
          </div>
        ) : previewResult ? (
          <div className="space-y-3">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Base Fee ({previewResult.chargeModel})</span>
              <span className="font-mono">{formatCurrency(previewResult.baseFee)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">VAT (15%)</span>
              <span className="font-mono">{formatCurrency(previewResult.vatAmount)}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-sm font-medium">
              <span>Total Fee</span>
              <span className="font-mono text-primary">{formatCurrency(previewResult.totalFee)}</span>
            </div>
            
            {previewResult.whoPays === "both" && (
              <>
                <Separator />
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Issuer Pays</span>
                    <span className="font-mono text-green-400">{formatCurrency(previewResult.issuerPays)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Acquirer Pays</span>
                    <span className="font-mono text-blue-400">{formatCurrency(previewResult.acquirerPays)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground text-center py-4">
            Enter values to preview fee
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="fee-schedules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Fee Schedules</h1>
          <p className="text-muted-foreground">Manage pricing plans and fee structures for counterparties</p>
        </div>
        
        <Button onClick={openCreateModal} className="shadow-[0_0_15px_rgba(59,130,246,0.3)]" data-testid="create-fee-schedule-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Fee Schedule
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Type:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {COUNTERPARTY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Status:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Schedules Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Counterparty</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Fee Lines</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : schedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <DollarSign className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">No fee schedules found</p>
                    <Button variant="link" onClick={openCreateModal} className="mt-2">
                      Create your first fee schedule
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                schedules.map((schedule) => (
                  <TableRow key={schedule.id} data-testid={`schedule-row-${schedule.id}`}>
                    <TableCell>
                      <div className="font-medium">{schedule.name}</div>
                      {schedule.notes && (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">{schedule.notes}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {COUNTERPARTY_TYPES.find(t => t.value === schedule.counterparty_type)?.label || schedule.counterparty_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {schedule.counterparty_name || schedule.counterparty_id || <span className="text-muted-foreground">Default</span>}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={schedule.status} />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {schedule.effective_from && new Date(schedule.effective_from).toLocaleDateString()}
                        {schedule.effective_to && (
                          <span className="text-muted-foreground"> → {new Date(schedule.effective_to).toLocaleDateString()}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {schedule.fee_lines?.length || 0} lines
                        {schedule.fee_lines?.[0] && (
                          <span className="text-muted-foreground ml-1">
                            ({formatCurrency(schedule.fee_lines[0].fixed_amount)})
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`schedule-actions-${schedule.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(schedule)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openCloneModal(schedule)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Clone
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAuditLog(schedule)}>
                            <History className="h-4 w-4 mr-2" />
                            Audit Log
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          
                          {schedule.status === "draft" && (
                            <DropdownMenuItem onClick={() => handleSubmitForApproval(schedule)}>
                              <Send className="h-4 w-4 mr-2 text-orange-400" />
                              Submit for Approval
                            </DropdownMenuItem>
                          )}
                          
                          {(schedule.status === "draft" || schedule.status === "pending_approval") && (
                            <DropdownMenuItem onClick={() => openApproveModal(schedule)}>
                              <Shield className="h-4 w-4 mr-2 text-green-400" />
                              Approve & Activate
                            </DropdownMenuItem>
                          )}
                          
                          {schedule.status === "active" && (
                            <DropdownMenuItem onClick={() => handleRetire(schedule)}>
                              <XCircle className="h-4 w-4 mr-2 text-gray-400" />
                              Retire
                            </DropdownMenuItem>
                          )}
                          
                          {schedule.status !== "active" && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(schedule)} className="text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-3">How Fee Schedules Work</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span><strong>Default schedules</strong> (no specific counterparty) apply to all partners of that type</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span><strong>Counterparty-specific schedules</strong> override the default for that partner</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span>Only <strong>Active</strong> schedules are used for fee calculations</span>
            </li>
            <li className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 mt-0.5 text-primary" />
              <span><strong>Workflow:</strong> Draft → Submit → Approve → Active</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Create/Edit Modal (2-Step) */}
      <Dialog open={isCreateModalOpen} onOpenChange={(open) => {
        setIsCreateModalOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingSchedule ? "Edit Fee Schedule" : "Create Fee Schedule"}
              <Badge variant="outline" className="ml-2">Step {currentStep} of 2</Badge>
            </DialogTitle>
            <DialogDescription>
              {currentStep === 1 ? "Configure schedule header details" : "Define fee lines and preview calculations"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            {currentStep === 1 ? (
              /* Step 1: Schedule Header */
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Schedule Name *</Label>
                    <Input
                      value={headerForm.name}
                      onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })}
                      placeholder="e.g., FNB Pilot, ABSA v2"
                      data-testid="schedule-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={headerForm.currency} onValueChange={(v) => setHeaderForm({ ...headerForm, currency: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ZAR">ZAR - South African Rand</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Counterparty Type *</Label>
                    <Select value={headerForm.counterparty_type} onValueChange={(v) => setHeaderForm({ ...headerForm, counterparty_type: v, counterparty_id: "" })}>
                      <SelectTrigger data-testid="counterparty-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTERPARTY_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Specific Counterparty (Optional)</Label>
                    <Select value={headerForm.counterparty_id || "__default__"} onValueChange={(v) => setHeaderForm({ ...headerForm, counterparty_id: v === "__default__" ? "" : v })}>
                      <SelectTrigger data-testid="counterparty-select">
                        <SelectValue placeholder="Default for all of type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__default__">Default (All {headerForm.counterparty_type}s)</SelectItem>
                        {getCounterpartyOptions().map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">If set, overrides the default for this specific partner</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Effective From *</Label>
                    <Input
                      type="date"
                      value={headerForm.effective_from}
                      onChange={(e) => setHeaderForm({ ...headerForm, effective_from: e.target.value })}
                      data-testid="effective-from-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Effective To (Optional)</Label>
                    <Input
                      type="date"
                      value={headerForm.effective_to}
                      onChange={(e) => setHeaderForm({ ...headerForm, effective_to: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Monthly Fee (ZAR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={headerForm.minimum_monthly}
                      onChange={(e) => setHeaderForm({ ...headerForm, minimum_monthly: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={headerForm.notes}
                    onChange={(e) => setHeaderForm({ ...headerForm, notes: e.target.value })}
                    placeholder="Contract reference, negotiation notes..."
                    rows={2}
                  />
                </div>
              </div>
            ) : (
              /* Step 2: Fee Lines + Preview */
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 py-4">
                <div className="lg:col-span-2">
                  {renderFeeLineTable()}
                </div>
                <div className="lg:col-span-1">
                  {renderPreviewPanel()}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 pt-4 border-t gap-2 flex-wrap">
            {currentStep === 1 ? (
              <>
                <Button type="button" variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => setCurrentStep(2)} disabled={!headerForm.name} data-testid="next-step-btn">
                  Next: Fee Lines
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={() => setCurrentStep(1)}>
                  Back
                </Button>
                <div className="flex-1" />
                <Button type="button" variant="outline" onClick={() => handleSubmit(true)} data-testid="save-draft-btn">
                  <Clock className="h-4 w-4 mr-2" />
                  Save as Draft
                </Button>
                <Button type="button" onClick={() => handleSubmit(false)} data-testid="submit-approval-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Submit for Approval
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Modal */}
      <Dialog open={isCloneModalOpen} onOpenChange={(open) => {
        setIsCloneModalOpen(open);
        if (!open) {
          setSelectedSchedule(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clone Fee Schedule</DialogTitle>
            <DialogDescription>Create a copy of "{selectedSchedule?.name}"</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Schedule Name *</Label>
              <Input
                value={headerForm.name}
                onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })}
                placeholder="Enter new schedule name"
              />
            </div>
            <div className="space-y-2">
              <Label>Effective From</Label>
              <Input
                type="date"
                value={headerForm.effective_from}
                onChange={(e) => setHeaderForm({ ...headerForm, effective_from: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCloneModalOpen(false)}>Cancel</Button>
            <Button onClick={handleClone} disabled={!headerForm.name}>
              <Copy className="h-4 w-4 mr-2" />
              Clone Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate Modal */}
      <Dialog open={isActivateModalOpen} onOpenChange={(open) => {
        setIsActivateModalOpen(open);
        if (!open) setSelectedSchedule(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Activate Fee Schedule</DialogTitle>
            <DialogDescription>Set effective dates for "{selectedSchedule?.name}"</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Effective From *</Label>
              <Input
                type="date"
                value={headerForm.effective_from}
                onChange={(e) => setHeaderForm({ ...headerForm, effective_from: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Effective To (Optional)</Label>
              <Input
                type="date"
                value={headerForm.effective_to}
                onChange={(e) => setHeaderForm({ ...headerForm, effective_to: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Leave empty for no end date</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsActivateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleActivate} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={isApproveModalOpen} onOpenChange={(open) => {
        setIsApproveModalOpen(open);
        if (!open) setSelectedSchedule(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve Fee Schedule</DialogTitle>
            <DialogDescription>Review and approve "{selectedSchedule?.name}"</DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Counterparty Type</span>
                <span>{selectedSchedule?.counterparty_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Fee Lines</span>
                <span>{selectedSchedule?.fee_lines?.length || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Effective From</span>
                <span>{selectedSchedule?.effective_from && new Date(selectedSchedule.effective_from).toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className="flex items-start gap-2 text-sm text-yellow-400 bg-yellow-500/10 p-3 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Approving will activate this schedule immediately. Ensure all fee lines are correct.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsApproveModalOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
              <Shield className="h-4 w-4 mr-2" />
              Approve & Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Modal */}
      <Dialog open={isAuditLogOpen} onOpenChange={(open) => {
        setIsAuditLogOpen(open);
        if (!open) setSelectedSchedule(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Audit Log
            </DialogTitle>
            <DialogDescription>History for "{selectedSchedule?.name}"</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4 py-4">
              {auditLogs.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No audit logs available</p>
              ) : (
                auditLogs.map((log, index) => (
                  <div key={index} className="flex gap-3 border-l-2 border-primary/30 pl-4 pb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{log.action}</span>
                        <span className="text-xs text-muted-foreground">by {log.user}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {log.timestamp && new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAuditLogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeeSchedules;
